/**
 * Path-aware PWA Web App Manifest (SSoT).
 *
 * @module lib/pwa-manifest
 */
import { MEMBERS_BALIE_CANONICAL_PATH, MEMBERS_DESK_CANONICAL_PATH } from "./build-members-balie-url";

/** Whitelisted query params preserved in start_url */
export const PWA_ALLOWED_QUERY_KEYS = ["tenant", "lang", "source"] as const;

export interface PwaManifestTenantContext {
  slug: string;
  customDomain?: string | null;
  storeName?: string | null;
  primaryColor?: string | null;
  backgroundColor?: string | null;
}

export interface WebAppManifestJson {
  name: string;
  short_name: string;
  description: string;
  start_url: string;
  scope: string;
  id: string;
  display: "standalone";
  orientation: "portrait-primary";
  background_color: string;
  theme_color: string;
  icons: Array<{
    src: string;
    sizes: string;
    type: string;
    purpose: string;
  }>;
  categories: string[];
  lang: string;
  dir: "ltr";
}

const BLOCKED_PATH_PREFIXES = [
  "/api/",
  "/assets/",
  "/design/",
  "/icons/",
  "/favicon",
] as const;

/**
 * Normalize pathname for PWA start_url (no traversal, leading slash).
 */
export function normalizePwaPathname(pathname: string): string {
  const trimmed = pathname.trim();
  if (!trimmed || trimmed === "") {
    return "/";
  }
  let base = trimmed.split("?")[0]?.split("#")[0] ?? trimmed;
  if (!base.startsWith("/")) {
    base = `/${base}`;
  }
  if (base.includes("..") || base.includes("//")) {
    return "/";
  }
  const collapsed = base.replace(/\/+/g, "/");
  if (collapsed.length > 1 && collapsed.endsWith("/")) {
    return collapsed.slice(0, -1);
  }
  return collapsed || "/";
}

function isBlockedPwaPath(pathname: string): boolean {
  if (pathname === "/") {
    return false;
  }
  return BLOCKED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Validate and normalize a full path input (pathname + optional search).
 */
export function resolvePwaStartPath(rawPath: string | null | undefined): string {
  if (!rawPath || typeof rawPath !== "string") {
    return "/";
  }
  const decoded = (() => {
    try {
      return decodeURIComponent(rawPath.trim());
    } catch {
      return rawPath.trim();
    }
  })();

  let pathname = decoded;
  let search = "";
  const qIndex = decoded.indexOf("?");
  if (qIndex >= 0) {
    pathname = decoded.slice(0, qIndex);
    search = decoded.slice(qIndex);
  }

  pathname = normalizePwaPathname(pathname);
  if (isBlockedPwaPath(pathname)) {
    return "/";
  }

  if (!search) {
    return pathname;
  }

  const params = new URLSearchParams(search);
  const filtered = new URLSearchParams();
  for (const key of PWA_ALLOWED_QUERY_KEYS) {
    const value = params.get(key);
    if (value !== null && value !== "") {
      filtered.set(key, value);
    }
  }
  const query = filtered.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function filterPwaSearchParams(
  searchParams: URLSearchParams,
): URLSearchParams {
  const filtered = new URLSearchParams();
  for (const key of PWA_ALLOWED_QUERY_KEYS) {
    const value = searchParams.get(key);
    if (value !== null && value !== "") {
      filtered.set(key, value);
    }
  }
  return filtered;
}

export function buildPwaStartUrlFromLocation(
  pathname: string,
  searchParams: URLSearchParams,
): string {
  const normalizedPath = normalizePwaPathname(pathname);
  if (isBlockedPwaPath(normalizedPath)) {
    return "/";
  }
  const filtered = filterPwaSearchParams(searchParams);
  const query = filtered.toString();
  return query ? `${normalizedPath}?${query}` : normalizedPath;
}

export function getPwaManifestHref(startPath: string): string {
  const resolved = resolvePwaStartPath(startPath);
  return `/api/pwa/manifest.json?path=${encodeURIComponent(resolved)}`;
}

export function getPwaManifestId(startUrl: string): string {
  return startUrl.startsWith("/") ? startUrl : `/${startUrl}`;
}

/** Surface kind for PWA display name and icon selection. */
export type PwaSurfaceIconKind =
  | "admin"
  | "pos"
  | "check-in"
  | "members-desk"
  | "storefront";

const PWA_MANIFEST_ICON_SIZES = [192, 512] as const;

function pwaManifestIconPaths(kind: PwaSurfaceIconKind): string[] {
  const prefix =
    kind === "storefront" ? "icon" : `${kind}-icon`;
  return PWA_MANIFEST_ICON_SIZES.map((size) => `/icons/${prefix}-${size}.png`);
}

function buildPwaManifestIcons(
  kind: PwaSurfaceIconKind,
): WebAppManifestJson["icons"] {
  return pwaManifestIconPaths(kind).map((src) => {
    const sizeMatch = src.match(/-(\d+)\.png$/);
    const size = sizeMatch?.[1] ?? "192";
    return {
      src,
      sizes: `${size}x${size}`,
      type: "image/png",
      purpose: "any maskable",
    };
  });
}

/**
 * Classify PWA surface from start_url pathname (SSoT for name + icon selection).
 */
export function resolvePwaSurfaceIconKind(startUrl: string): PwaSurfaceIconKind {
  const pathname = startUrl.split("?")[0] ?? startUrl;

  if (
    pathname === MEMBERS_DESK_CANONICAL_PATH ||
    pathname.startsWith(`${MEMBERS_DESK_CANONICAL_PATH}/`) ||
    pathname === MEMBERS_BALIE_CANONICAL_PATH ||
    pathname.startsWith(`${MEMBERS_BALIE_CANONICAL_PATH}/`)
  ) {
    return "members-desk";
  }

  if (/^\/pos\/(\d+)/.test(pathname)) {
    return "pos";
  }

  if (/^\/admin\/check-in\/(\d+)/.test(pathname)) {
    return "check-in";
  }

  if (pathname.startsWith("/admin")) {
    return "admin";
  }

  return "storefront";
}

function buildPwaDisplayName(
  tenant: PwaManifestTenantContext,
  startUrl: string,
): { name: string; short_name: string } {
  const storeName = tenant.storeName?.trim() || "Pagayo";
  const pathname = startUrl.split("?")[0] ?? startUrl;
  const kind = resolvePwaSurfaceIconKind(startUrl);

  switch (kind) {
    case "members-desk":
      return {
        name: `Members Desk — ${storeName}`,
        short_name: storeName.substring(0, 12),
      };
    case "pos": {
      const posMatch = pathname.match(/^\/pos\/(\d+)/);
      const terminalId = posMatch?.[1] ?? "1";
      return {
        name: `POS ${terminalId} — ${storeName}`,
        short_name: `POS ${terminalId}`.substring(0, 12),
      };
    }
    case "check-in": {
      const checkInMatch = pathname.match(/^\/admin\/check-in\/(\d+)/);
      const scannerId = checkInMatch?.[1] ?? "1";
      return {
        name: `Check-in ${scannerId} — ${storeName}`,
        short_name: `Scan ${scannerId}`.substring(0, 12),
      };
    }
    case "admin":
      return {
        name: `Admin — ${storeName}`,
        short_name: storeName.substring(0, 12),
      };
    default:
      return {
        name: storeName,
        short_name: storeName.substring(0, 12),
      };
  }
}

export function buildPwaManifest(
  tenant: PwaManifestTenantContext,
  rawStartPath: string | null | undefined,
): WebAppManifestJson {
  const start_url = resolvePwaStartPath(rawStartPath);
  const primaryColor = tenant.primaryColor?.trim() || "#4F46E5";
  const backgroundColor = tenant.backgroundColor?.trim() || "#FFFFFF";
  const surfaceKind = resolvePwaSurfaceIconKind(start_url);
  const { name, short_name } = buildPwaDisplayName(tenant, start_url);
  // Keep the manifest id same-origin so browsers retain it on staging/local/custom domains.
  const id = getPwaManifestId(start_url);
  const isAdminOrTerminal = surfaceKind !== "storefront";

  return {
    name,
    short_name,
    description: isAdminOrTerminal
      ? `${storeNameFromTenant(tenant)} — werkplek`
      : `${storeNameFromTenant(tenant)} — Webshop`,
    start_url,
    scope: "/",
    id,
    display: "standalone",
    orientation: "portrait-primary",
    background_color: backgroundColor,
    theme_color: primaryColor,
    icons: buildPwaManifestIcons(surfaceKind),
    categories: isAdminOrTerminal
      ? ["business", "productivity"]
      : ["shopping", "lifestyle"],
    lang: "nl",
    dir: "ltr",
  };
}

function storeNameFromTenant(tenant: PwaManifestTenantContext): string {
  return tenant.storeName?.trim() || "Pagayo";
}

/** Build start path from an incoming HTTP request (pathname + whitelisted query). */
export function buildPwaStartPathFromRequestUrl(requestUrl: string): string {
  const url = new URL(requestUrl);
  return buildPwaStartUrlFromLocation(url.pathname, url.searchParams);
}
